<script setup lang="ts">
import { computed, onMounted, PropType, reactive, ref, watch, watchEffect } from "vue";
import {
    Department,
    Project,
    PseudonymMode,
    Recipients,
    Trainer,
    TranslatedProduct,
} from "../../../types/PersologOnlineTypes";
import TooltipButton from "../../common/TooltipButton.vue";
import { useI18n } from "vue-i18n";
import { i18n } from "../../../main";
import {
    formatMonthPeriod,
    getDisplayName,
    getRecipientName,
    isValidEmail,
    parseMonthPeriod
} from "../../../utils/GeneralUtils";
import { parseServerDate, parseServerDateOnly, toServerDateOnly } from "@persolog/utils";
import { focusMapping, getDepartments, getProductNames, productHasBenchmarkDiagram } from "../../../utils/ProjectUtils";
import {
    allowedRecipients,
    isAdditionalRecipientComplete,
    isAdditionalRecipientInvalid,
    isRecipientFullyLocked,
    isRecipientUpgradeOnly,
    isSelectable as selectableRecipient,
    resolveSelectedLocale,
} from "../../../utils/ProjectConfigUtils";
import { ProfileAdministrationAPI, QuestionnaireAPI } from "../../../api";
import { GENERATED_PSEUDONYMS_FLAG, useFeatureFlag } from "../../../composables/useFeatureFlag.ts";

const { t } = useI18n();

/**
 * Project configuration component
 * @property {TranslatedProduct} product
 * @property {TranslatedProduct[]} products
 * @property {Project} project - The project to be configured (editable state passed from parent component)
 * @property {Trainer} trainer - The trainer who is creating the project
 * @property {Department[]} departments
 * @property {boolean} hasActiveParticipants - True once any participant advanced beyond CREATED/REVOKED; locks the additional recipient and, unless the project is still trainer-only, the recipient itself
 * @property {boolean} productEditable
 * @property {boolean} hasDirectLink - Shows the anonymous switch if true
 * @property {boolean} anonymousSwitchDisabled - Disables the anonymous switch, independent of hasActiveParticipants and from the editable project state
 */
const props = defineProps({
    product: {
        type: Object as PropType<TranslatedProduct>,
        required: true,
    },
    products: {
        type: Array as PropType<TranslatedProduct[]>,
        default: () => [],
    },
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
    departments: {
        type: Array as PropType<Department[]>,
        default: () => [],
    },
    focus: {
        type: String,
        default: "",
    },
    hasActiveParticipants: {
        type: Boolean,
        default: false,
    },
    productEditable: {
        type: Boolean,
    },
    hasDirectLink: {
        type: Boolean,
        default: false,
    },
    anonymousSwitchDisabled: {
        type: Boolean,
        default: false,
    },
});

const currentProject = ref<Project | undefined>(props.project);
const currentProduct = ref<TranslatedProduct | undefined>(props.product);
const defaultProducts = ref();

const selectedProduct = ref();
const selectedLanguage = ref();
const defaultProduct = ref();
const productNames = ref();

// pre-select if available
selectedProduct.value = currentProduct?.value?.productNo;
selectedLanguage.value = currentProject?.value?.language;

const projectName = ref<string>(
    currentProject.value?.name ??
        (currentProduct.value
            ? `${currentProduct.value?.productName} - ${new Date().toLocaleDateString(i18n.global.locale.value)}`
            : ""),
);

//Uses the focus from the current project, if available, otherwise uses the focus from the props
const projectFocus = ref<string>(currentProject.value?.focus ?? props.focus ?? "");

// Project name
const projectNameMaxLength = 75;
const projectNameLength = computed(() => projectName.value.length);

// Project description
const projectDescription = ref<string>(currentProject.value?.description ?? "");
const descriptionMaxLength = 1000;
const descriptionLength = computed(() => projectDescription.value.length);

const projectDepartments = ref<Department[]>(props.departments ?? []);
const projectAnonymous = ref<boolean>(currentProject.value?.anonymous ?? false);
// Only meaningful while projectAnonymous is on. GENERATED is the default for new projects because it
// is what most anonymous projects want: the pseudonym exists to keep real names out of the profile,
// and asking a participant to invent one produces "Batman" and duplicates. Self-chosen stays for
// studies, where the participant applies a reproducible rule so a follow-up survey can be matched.
// An existing project without a mode falls back to SELF_CHOSEN, the backend's default, rather than to
// the new-project default: its real mode is simply not loaded yet, and guessing GENERATED is what got
// unrelated edits refused once participants existed (RDT-426).
const pseudonymMode = ref<PseudonymMode>(
    currentProject.value?.pseudonymMode ??
        (currentProject.value?.id === undefined ? PseudonymMode.GENERATED : PseudonymMode.SELF_CHOSEN),
);
const generatedPseudonymsEnabled = useFeatureFlag(GENERATED_PSEUDONYMS_FLAG);
// The refs above are read once at setup, but the full project can arrive after the dialog opened (the
// overview hands over a lighter list item first). Follow its mode so the radio and the save use the
// stored value instead of the fallback.
watch(
    () => props.project?.pseudonymMode,
    (stored) => {
        if (stored) pseudonymMode.value = stored;
    },
);

const recipientEmail = ref<string | undefined>(currentProject.value?.additionalRecipient?.email);
const recipientFirstName = ref<string | undefined>(currentProject.value?.additionalRecipient?.firstName);
const recipientLastName = ref<string | undefined>(currentProject.value?.additionalRecipient?.lastName);

// Date (and time) at which a trainer-only project is automatically switched to "both" so participants
// receive their profile. Only meaningful while the recipient is the trainer.
const recipientReleaseDate = ref<Date | undefined>(
    currentProject.value?.recipientReleaseDate
        ? parseServerDate(currentProject.value.recipientReleaseDate)
        : undefined,
);

// The recipient the participant acknowledged when starting the questionnaire. Captured from the
// persisted project (not the in-progress selection, which watchEffect keeps overwriting) so the
// data-protection gating stays stable while editing.
const originalRecipient = (props.project?.recipients as Recipients) ?? Recipients.TRAINER;

const projectParticipantRetentionPeriod = ref<number>(
    parseMonthPeriod(props.project?.participantRetentionPeriod?.toString()) ?? 24,
);

const projectParticipantDeletionNotification = ref<boolean>(
    currentProject.value?.participantDeletionNotification ?? true,
);
// Date-only field: the stored value's UTC calendar day is authoritative, the time part is
// meaningless. Read/write it through the date-only helpers so open → save keeps the day stable.
const projectParticipantDeletionDate = ref<Date | undefined>(
    currentProject.value?.participantDeletionDate
        ? parseServerDateOnly(currentProject.value.participantDeletionDate)
        : undefined,
);

/**
 * Controls which retention option is selected: fixed date or month period
 */
const dataRetentionDateOption = ref(props.project?.participantDeletionDate ? "fixed_date" : "month_period");

if (currentProject.value?.id) {
    getDepartments(currentProject.value).then((result) => {
        projectDepartments.value = result;
    });
}

/**
 * Controls DatePicker min and max dates
 */
const today = new Date();
const minDate = ref(new Date());
const maxDate = ref(new Date());

minDate.value.setDate(today.getDate());
maxDate.value.setDate(today.getDate() + 3 * 365);

const invalidFlags = reactive({
    product: false,
    projectName: false,
    projectLanguage: false,
    projectRecipient: false,
    projectAdditionalRecipient: false,
});

const selectedLocale = computed(() =>
    resolveSelectedLocale(
        currentProduct.value?.profileLanguages,
        currentProject?.value?.language,
        props.trainer?.language,
        i18n.global.locale.value,
    ),
);

const hasFocus = ref(false);

const languages = computed(() => {
    const sortedLanguages = currentProduct.value?.profileLanguages.slice().sort();
    return sortedLanguages?.map((lang: string) => ({
        name: getDisplayName(i18n.global.locale.value, lang),
        code: lang,
    }));
});

/**
 * Once participants are active, data protection only allows widening the audience to include the
 * participant themselves (TRAINER -> BOTH). A trainer-only project can still be upgraded; a project
 * that already reaches the participant (PARTICIPANT/BOTH) is locked entirely.
 */
const recipientUpgradeOnly = computed(() =>
    isRecipientUpgradeOnly(props.hasActiveParticipants, originalRecipient),
);
const recipientFullyLocked = computed(() =>
    isRecipientFullyLocked(props.hasActiveParticipants, originalRecipient),
);

const recipients = computed(() =>
    allowedRecipients(
        props.hasActiveParticipants,
        originalRecipient,
        productHasBenchmarkDiagram(props.product?.productNo),
    ).map((recipient: Recipients) => ({
        name: getRecipientName(props.trainer, recipient),
        value: recipient,
    })),
);

/**
 * The focus key of a product: what its focus field means (a feedback recipient, a job, a team, …)
 */
const focusKeyFor = (productNo?: string) => (productNo && focusMapping[productNo]) || focusMapping.default;

/**
 * Determines the focus key based on the selected product
 */
const focusKey = computed(() => focusKeyFor(props.product?.productNo));

/**
 * Translated focus title
 */
const focusText = computed(() => {
    return t(`project-configuration.projectFocus.${focusKey.value}.title`);
});

/**
 * Translated focus info text
 */
const focusInfoText = computed(() => {
    return t(`project-configuration.projectFocus.${focusKey.value}.info`);
});

const selectedRecipient = ref({
    name: getRecipientName(
        props.trainer,
        (currentProject?.value?.recipients as Recipients) ?? isSelectable(props.trainer?.recipient),
    ),
    value: currentProject?.value?.recipients ?? isSelectable(props.trainer?.recipient),
});

function isSelectable(recipient: string) {
    return selectableRecipient(
        recipient,
        recipients.value.map((option) => option.value),
    );
}

const emit = defineEmits(["update:project", "update:valid"]);

// set selected product if dropdown changes
watch(
    () => selectedProduct.value,
    () => {
        currentProduct.value =
            props.products?.find((product: TranslatedProduct) => product.productNo == selectedProduct.value) ||
            undefined;
        selectedLanguage.value = selectedLocale.value;
    },
);

/**
 * Automatically generates a product name if selected product changes
 * and fetches the questionnaire to determine if the product has a focus.
 * The questionnaire shows a project focus to every participant as already given, so a focus
 * entered for another product must not survive the switch: the same field is the feedback
 * recipient on a 361 and e.g. "Ich bei der Arbeit" elsewhere (RDT-464). It is kept only when
 * the new product's focus means the same, and dropped when the new product has none.
 */
watch(
    () => currentProduct.value,
    async (newProduct, oldProduct) => {
        projectName.value = `${currentProduct.value?.productName} - ${new Date().toLocaleDateString(i18n.global.locale.value)}`;
        selectedLanguage.value = selectedLocale.value;
        if (focusKeyFor(newProduct?.productNo) !== focusKeyFor(oldProduct?.productNo)) {
            projectFocus.value = "";
        }
        const questionnaire = await QuestionnaireAPI.getQuestionnaire(currentProduct.value!.productNo);
        hasFocus.value = questionnaire.hasFocus;
        if (!questionnaire.hasFocus) {
            projectFocus.value = "";
        }
    },
);

// select trainer as only recipient when project is made anonymous
watch(
    () => projectAnonymous.value,
    () => {
        if (projectAnonymous.value === true) {
            selectedRecipient.value = {
                name: getRecipientName(props.trainer, Recipients.TRAINER),
                value: Recipients.TRAINER,
            };
        }
    },
);

/**
 * Sets all project attributes when any of the values change, which is used to update an existing project,
 * or to create a new project.
 */
watchEffect(() => {
    if (currentProject.value === undefined) {
        const project: Project = {
            name: "",
            product: "",
            defaultProduct: "",
            language: selectedLocale.value,
            trainer: props.trainer?.id,
            // Preview value only: the backend stamps the real creation date server-side and
            // ignores this, but the wizard's preview step renders it before the project exists.
            creation: new Date().toISOString(),
            participantDeletionNotification: props.trainer.deletionNotification,
            participantRetentionPeriod: props.trainer.standardRetentionPeriod,
        };
        currentProject.value = project;
        selectedLanguage.value = project.language;
    }

    // set project attributes
    const currentProjectValue = currentProject.value;
    if (currentProduct.value) currentProjectValue.product = currentProduct.value.productNo;
    currentProjectValue.name = projectName.value.trim();
    currentProjectValue.description = projectDescription.value.trim();
    currentProjectValue.focus = projectFocus.value.trim();
    currentProjectValue.anonymous = projectAnonymous.value;
    // Only an anonymous project has a pseudonym mode to choose; on any other project it has no effect,
    // so whatever was loaded is passed through untouched.
    if (generatedPseudonymsEnabled.value && projectAnonymous.value) {
        currentProjectValue.pseudonymMode = pseudonymMode.value;
    }
    currentProjectValue.language = selectedLanguage.value;
    currentProjectValue.recipients = selectedRecipient.value.value as Recipients;
    if (defaultProduct.value) {
        currentProjectValue.defaultProduct = defaultProduct.value;
    }

    // If creating new project, fetch values from trainer settings for retention period and deletion notification
    if (currentProjectValue.id === undefined) {
        currentProjectValue.participantDeletionNotification = props.trainer.deletionNotification;
        currentProjectValue.participantRetentionPeriod = props.trainer.standardRetentionPeriod;
        projectParticipantDeletionNotification.value = props.trainer.deletionNotification;
        projectParticipantRetentionPeriod.value = parseMonthPeriod(props.trainer.standardRetentionPeriod) ?? 24;
    } else {
        currentProjectValue.participantDeletionNotification = projectParticipantDeletionNotification.value;
        currentProjectValue.participantRetentionPeriod = formatMonthPeriod(projectParticipantRetentionPeriod.value);
    }

    // Only set deletion date if fixed date option is selected. Otherwise, set it to null
    if (dataRetentionDateOption.value === "fixed_date" && projectParticipantDeletionDate.value) {
        currentProjectValue.participantDeletionDate = toServerDateOnly(projectParticipantDeletionDate.value);
    } else {
        currentProjectValue.participantDeletionDate = null;
    }

    if (isAdditionalRecipientComplete(recipientEmail.value, recipientFirstName.value, recipientLastName.value)) {
        currentProjectValue.additionalRecipient = {
            email: recipientEmail.value!,
            firstName: recipientFirstName.value!,
            lastName: recipientLastName.value!,
        };
    } else currentProjectValue.additionalRecipient = null;

    // A release date only applies while the trainer is the sole recipient; otherwise there is nothing
    // left to release automatically.
    currentProjectValue.recipientReleaseDate =
        selectedRecipient.value.value === Recipients.TRAINER && recipientReleaseDate.value
            ? recipientReleaseDate.value.toISOString()
            : null;

    invalidFlags.product = currentProjectValue.product == "";
    invalidFlags.projectName = currentProjectValue.name == "";
    invalidFlags.projectLanguage = currentProjectValue.language == "" || currentProjectValue.language == undefined;
    invalidFlags.projectRecipient = !currentProjectValue.recipients;
    invalidFlags.projectAdditionalRecipient = isAdditionalRecipientInvalid(
        recipientEmail.value,
        recipientFirstName.value,
        recipientLastName.value,
        recipientEmail.value ? isValidEmail(recipientEmail.value) : false,
    );

    emit("update:project", currentProject.value);
    emit("update:valid", !Object.values(invalidFlags).includes(true));
});

/**
 * Checks if project already has a selected product and fetches the questionnaire
 * to determine if the project has a focus
 */
onMounted(async () => {
    // Creating project from Projects Overview
    if (currentProject.value && currentProject.value?.product !== currentProject.value?.defaultProduct) {
        productNames.value = await getProductNames();
        const groupCharts = await ProfileAdministrationAPI.getBaseProductsForGroupChart(currentProject.value?.product);
        if (groupCharts && groupCharts.length > 0) {
            defaultProducts.value = groupCharts[0].products.products.map((product) => ({
                productName: productNames.value[product.productNo],
                productNo: product.productNo,
            }));
        }
    }
    defaultProduct.value = props.project?.defaultProduct;
    if (currentProduct.value === undefined || currentProduct.value == null) {
        hasFocus.value = false;
        return;
    }
    const questionnaire = await QuestionnaireAPI.getQuestionnaire(currentProduct.value!.productNo);
    hasFocus.value = questionnaire.hasFocus;
});

onMounted(() => {
    const panelHeaderElement = advancedSettingsPanel.value.$el.querySelector(".p-panel-header");
    if (panelHeaderElement) panelHeaderElement.addEventListener("click", toggleAdvancedSettings, { capture: true });
});

const advancedSettingsPanel = ref();
const toggleAdvancedSettings = (e: Event) => {
    e.stopPropagation();
    advancedSettingsPanel.value.toggle(e);
};
</script>

<template>
    <div class="flex flex-wrap gap-4 pt-2">
        <!-- Product -->
        <div v-if="productEditable" class="basis-full md:flex-1 mb-4">
            <FloatLabel class="w-full">
                <Select
                    v-model="selectedProduct"
                    :options="products"
                    filter
                    option-label="productName"
                    option-value="productNo"
                    :class="['w-full', { 'p-invalid': invalidFlags.product }]"
                    :id="`project-config_drop-down_product-${currentProject?.id ?? 0}`"
                />
                <label :for="`project-config_drop-down_product-${currentProject?.id ?? 0}`">
                    {{ t("project-configuration.product.label") }}
                </label>
            </FloatLabel>
        </div>

        <!-- Default Product -->
        <div
            v-if="project && project.defaultProduct && project.product !== project.defaultProduct"
            class="basis-full md:flex-1 mb-4"
        >
            <FloatLabel class="w-full">
                <Select
                    v-model="defaultProduct"
                    :options="defaultProducts"
                    filter
                    option-label="productName"
                    option-value="productNo"
                    :class="['w-full', { 'p-invalid': invalidFlags.product }]"
                    :id="`project-config_drop-down_default_product-${currentProject?.id ?? 0}`"
                />
                <label :for="`project-config_drop-down_default_product-${currentProject?.id ?? 0}`">
                    {{ t("project-details.defaultProduct.label-default") }}
                </label>
            </FloatLabel>
        </div>

        <!-- Language -->
        <div class="basis-full md:flex-1 mb-4 mt-4 md:mt-0">
            <FloatLabel class="w-full">
                <Select
                    v-model="selectedLanguage"
                    :options="languages"
                    filter
                    option-label="name"
                    option-value="code"
                    :class="['w-full', { 'p-invalid': invalidFlags.projectLanguage }]"
                    :id="`project-config_drop-down_language-${currentProject?.id ?? 0}`"
                />
                <label :for="`project-config_drop-down_language-${currentProject?.id ?? 0}`">
                    {{ t("project-configuration.language.label") }}
                </label>
            </FloatLabel>
        </div>

        <!-- Focus -->
        <div class="basis-full md:flex-1 mb-4 w-full mt-4 md:mt-0">
            <InputGroup>
                <IconField class="w-full mr-2">
                    <FloatLabel>
                        <InputText
                            :id="`project-config_input-text_focus-${currentProject?.id ?? 0}`"
                            type="text"
                            v-model="projectFocus"
                            class="w-full"
                            :disabled="!hasFocus"
                        />
                        <label :for="`project-config_input-text_focus-${currentProject?.id ?? 0}`">
                            {{ focusText }}
                        </label>
                        <InputIcon class="pi pi-eye" />
                    </FloatLabel>
                </IconField>
                <TooltipButton :content="focusInfoText" :disabled="!hasFocus" background-color="white" />
            </InputGroup>
        </div>
    </div>

    <!-- Project title -->
    <div class="flex flex-wrap gap-4 mt-4">
        <div class="flex-1 mb-4">
            <IconField>
                <FloatLabel class="w-full">
                    <InputText
                        type="text"
                        v-model="projectName"
                        :class="['w-full', { 'p-invalid': invalidFlags.projectName }]"
                        :maxlength="projectNameMaxLength"
                        :id="`project-config_input-text_name-${currentProject?.id ?? 0}`"
                    />
                    <label :for="`project-config_input-text_name-${currentProject?.id ?? 0}`">
                        {{ t("project-configuration.projectName.label") }}
                    </label>
                    <small class="textarea-length">
                        <span
                            :id="`project-name_length-${currentProject?.id ?? 0}`"
                            :class="{ 'text-red': projectName.length === projectNameMaxLength }"
                        >
                            {{ projectNameLength }}
                        </span>
                        /{{ projectNameMaxLength }}
                    </small>
                </FloatLabel>
                <small><i class="fa fa-info-circle"></i>{{ t("project-configuration.projectName.info") }}</small>
            </IconField>
        </div>
    </div>

    <!-- Project description -->
    <div class="flex flex-wrap gap-4 mt-4">
        <div class="flex-1 mb-4">
            <IconField>
                <FloatLabel class="w-full">
                    <Textarea
                        :id="`project-config_text-area_description-${currentProject?.id ?? 0}`"
                        rows="3"
                        cols="60"
                        v-model="projectDescription"
                        :class="{ 'invalid-textarea': descriptionLength > descriptionMaxLength }"
                        class="w-full"
                        :invalid="descriptionLength > descriptionMaxLength"
                    />
                    <small class="textarea-length">{{ descriptionLength }}/{{ descriptionMaxLength }}</small>
                    <small style="color: red" v-if="descriptionLength > descriptionMaxLength">
                        {{ t("project-configuration.projectDescription.maxLengthReached") }}
                    </small>
                    <label :for="`project-config_text-area_description-${currentProject?.id ?? 0}`">
                        {{ t("project-configuration.projectDescription.label") }}
                    </label>
                    <InputIcon class="pi pi-align-left" />
                </FloatLabel>
            </IconField>
        </div>
    </div>

    <!-- Select recipients + optional release date (side by side on desktop, stacked on mobile) -->
    <div class="flex flex-wrap gap-4 mt-4">
        <div class="basis-full md:basis-[calc(50%-0.5rem)] mb-4">
            <label for="recipients" class="switch-label">{{ t("project-configuration.recipients.label") }}</label>
            <div class="flex items-center mt-1">
                <Select
                    v-model="selectedRecipient"
                    :options="recipients"
                    option-label="name"
                    :placeholder="t('project-configuration.recipients.placeholder')"
                    :class="invalidFlags.projectRecipient ? 'p-invalid w-full' : 'w-full'"
                    :id="`project-config_drop-down_recipients-${currentProject?.id ?? 0}`"
                    :disabled="recipientFullyLocked || projectAnonymous"
                >
                    <template #value="slotProps">
                        <div v-if="slotProps.value" class="flex items-center">
                            <div>{{ slotProps.value.name }}</div>
                        </div>
                        <span v-else>{{ slotProps.placeholder }}</span>
                    </template>
                    <template #option="slotProps">
                        <div class="flex items-center">
                            <div>{{ slotProps.option.name }}</div>
                        </div>
                    </template>
                </Select>
                <TooltipButton
                    :content="
                        projectAnonymous
                            ? t('project-configuration.anonymous.participant-only')
                            : t('project-configuration.recipients.explained')
                    "
                    background-color="white"
                />
            </div>
        </div>

        <!-- Recipient release date: automatically switch a trainer-only project to 'both' at this time -->
        <div v-if="selectedRecipient.value === 'TRAINER' && !projectAnonymous" class="basis-full md:basis-[calc(50%-0.5rem)] mb-4">
            <label for="recipient-release-date" class="switch-label">
                {{ t("project-configuration.recipients.releaseDate.label") }}
            </label>
            <div class="flex items-center mt-1">
                <DatePicker
                    v-model="recipientReleaseDate"
                    :placeholder="t('project-configuration.recipients.releaseDate.placeholder')"
                    :id="`project-config_input-datepicker_recipient-release-date-${currentProject?.id ?? 0}`"
                    class="md:p-0 input-box w-full"
                    :date-format="t('formats.datePicker')"
                    :min-date="minDate"
                    show-time
                    hour-format="24"
                    show-icon
                    show-button-bar
                />
                <TooltipButton
                    :content="t('project-configuration.recipients.releaseDate.explained')"
                    background-color="white"
                />
            </div>
        </div>
    </div>

    <!-- Warning participant only selected -->
    <div v-if="selectedRecipient.value === 'PARTICIPANT'">
        <Message :closable="false">
            <span v-html="t('project-configuration.recipients.participant-only-explained')"></span>
        </Message>
    </div>

    <!-- Trainer-only project with active participants: only widening to 'both' is possible -->
    <div v-if="recipientUpgradeOnly">
        <Message :closable="false" severity="info">
            <span>{{ t("project-configuration.recipients.upgrade-only-explained") }}</span>
        </Message>
    </div>

    <!-- Advanced Settings -->
    <Panel
        :header="t('project-configuration.advanced-settings.label')"
        :collapsed="true"
        toggleable
        ref="advancedSettingsPanel"
        :pt="{
            title: { style: 'font-weight: bold; font-size:small' },
        }"
        :id="`project-config_advanced-settings-panel-${currentProject?.id ?? 0}`"
    >
        <div class="flex items-center mt-4" style="height: 30px">
            <h4 style="margin: 0">{{ t("project-configuration.additionalRecipient.label") }}</h4>
            <TooltipButton
                background-color="transparent"
                :content="t('project-configuration.recipients.additional-explained')"
            />
        </div>
        <div class="flex flex-wrap md:flex-wrap">
            <div class="flex items-center justify-center my-1 mr-2">
                <IconField>
                    <InputText
                        :id="`project-config_input-text_recipient-email-${currentProject?.id ?? 0}`"
                        :disabled="hasActiveParticipants || projectAnonymous"
                        type="email"
                        v-model="recipientEmail"
                        :class="['w-full', { 'p-invalid': invalidFlags.projectAdditionalRecipient }]"
                        :placeholder="t('project-configuration.additionalRecipient.email')"
                    />
                    <InputIcon class="pi pi-envelope" />
                </IconField>
            </div>
            <div class="flex items-center justify-center my-1 mr-2">
                <IconField>
                    <InputText
                        :id="`project-config_input-text_recipient-firstname-${currentProject?.id ?? 0}`"
                        :disabled="hasActiveParticipants || projectAnonymous"
                        type="text"
                        v-model="recipientFirstName"
                        :class="['w-full', { 'p-invalid': invalidFlags.projectAdditionalRecipient }]"
                        :placeholder="t('project-configuration.additionalRecipient.firstName')"
                    />
                    <InputIcon class="pi pi-user" />
                </IconField>
            </div>
            <div class="flex items-center justify-center my-1 mr-2">
                <IconField>
                    <InputText
                        :id="`project-config_input-text_recipient-lastname-${currentProject?.id ?? 0}`"
                        :disabled="hasActiveParticipants || projectAnonymous"
                        type="text"
                        v-model="recipientLastName"
                        :class="['w-full', { 'p-invalid': invalidFlags.projectAdditionalRecipient }]"
                        :placeholder="t('project-configuration.additionalRecipient.lastName')"
                    />
                    <InputIcon class="pi pi-user" />
                </IconField>
            </div>
        </div>
        <Divider />
        <!-- Anonymous Switch Button -->
        <div v-if="hasDirectLink" class="mr-12 mt-4">
            <h4 style="margin: 0">{{ t("project-configuration.anonymous.input-label") }}</h4>
            <div class="flex items-center">
                <ToggleSwitch
                    :id="`project-config_input-switch_anonymous-${currentProject?.id ?? 0}`"
                    v-model="projectAnonymous"
                    class="custom-switch"
                    :disabled="hasActiveParticipants || props.anonymousSwitchDisabled"
                />
                <TooltipButton :content="t('project-configuration.anonymous.help')" background-color="white" />
            </div>
            <!--
                Locked once anyone has registered, like the anonymous switch itself: the participants
                already in the project got their pseudonyms one way, and switching would leave a list
                where some chose their own and the rest were assigned one, with nothing to tell them
                apart. The backend enforces the same rule.
            -->
            <div v-if="projectAnonymous && generatedPseudonymsEnabled" class="mt-3 ml-1">
                <div class="flex items-center gap-2 mb-2">
                    <RadioButton
                        v-model="pseudonymMode"
                        input-id="pseudonym-generated"
                        name="pseudonymMode"
                        :value="PseudonymMode.GENERATED"
                        :id="`project-config_input-radio_pseudonym-generated-${currentProject?.id ?? 0}`"
                        :disabled="hasActiveParticipants || props.anonymousSwitchDisabled"
                        :aria-label="t('project-configuration.anonymous.pseudonym.generated')"
                    />
                    <label for="pseudonym-generated">
                        {{ t("project-configuration.anonymous.pseudonym.generated") }}
                    </label>
                </div>
                <div class="flex items-center gap-2">
                    <RadioButton
                        v-model="pseudonymMode"
                        input-id="pseudonym-self-chosen"
                        name="pseudonymMode"
                        :value="PseudonymMode.SELF_CHOSEN"
                        :id="`project-config_input-radio_pseudonym-self-chosen-${currentProject?.id ?? 0}`"
                        :disabled="hasActiveParticipants || props.anonymousSwitchDisabled"
                        :aria-label="t('project-configuration.anonymous.pseudonym.selfChosen')"
                    />
                    <label for="pseudonym-self-chosen">
                        {{ t("project-configuration.anonymous.pseudonym.selfChosen") }}
                    </label>
                    <TooltipButton
                        :content="t('project-configuration.anonymous.pseudonym.help')"
                        background-color="white" />
                </div>
            </div>
        </div>
        <Divider />
        <!-- Retention Period -->
        <div class="flex retention-period-box">
            <div class="mr-12 md:w-1/4 basis-10/12">
                <h4 class="flex-start-top mb-2">{{ t("project-configuration.retentionPeriod.header") }}</h4>
                <div class="flex row">
                    <div class="flex items-center gap-2 mr-2">
                        <RadioButton
                            v-model="dataRetentionDateOption"
                            input-id="fixed_date"
                            name="deletionOption"
                            value="fixed_date"
                            :id="`project-config_input-radio_fixed-date-${currentProject?.id ?? 0}`"
                            :aria-label="t('project-configuration.retentionPeriod.fixedDate')"
                        />
                        <label for="fixed_date">{{ t("project-configuration.retentionPeriod.fixedDate") }}</label>
                    </div>
                    <div class="flex items-center gap-2 mr-2">
                        <RadioButton
                            v-model="dataRetentionDateOption"
                            input-id="month_period"
                            name="deletionOption"
                            value="month_period"
                            :aria-label="t('project-configuration.retentionPeriod.monthPeriod')"
                            :id="`project-config_input-radio_month-period-${currentProject?.id ?? 0}`"
                        />
                        <label for="month_period">{{ t("project-configuration.retentionPeriod.monthPeriod") }}</label>
                    </div>
                </div>
                <div v-if="dataRetentionDateOption === 'fixed_date'" class="flex row mt-2">
                    <DatePicker
                        v-model="projectParticipantDeletionDate"
                        :disabled="dataRetentionDateOption !== 'fixed_date'"
                        placeholder="Select a date"
                        :id="`project-config_input-datepicker_deletion-date-${currentProject?.id ?? 0}`"
                        class="md:p-0 input-box"
                        :date-format="t('formats.datePicker')"
                        :min-date="minDate"
                        :max-date="maxDate"
                        show-icon
                    />
                    <TooltipButton
                        :content="t('project-configuration.retentionPeriod.fixedDateExplained')"
                        background-color="white"
                    />
                </div>
                <div v-else class="flex row mt-2">
                    <InputNumber
                        v-model="projectParticipantRetentionPeriod"
                        :disabled="dataRetentionDateOption !== 'month_period'"
                        button-layout="horizontal"
                        show-buttons
                        :min="1"
                        :max="36"
                        :id="`project-config_input-number_retention-period-${currentProject?.id ?? 0}`"
                        class="md:p-0 input-box"
                        :suffix="
                            ' ' +
                            t('project-configuration.retentionPeriod.value', {
                                count: projectParticipantRetentionPeriod,
                            })
                        "
                    />
                    <TooltipButton
                        :content="t('project-configuration.retentionPeriod.periodExplained', { count: projectParticipantRetentionPeriod })"
                        background-color="white"
                    />
                </div>
            </div>
            <!-- Participant deletion notification -->
            <div class="mr-12 md:w-1/4 basis-6/12 mr-auto">
                <h4 class="flex-start-top">{{ t("project-configuration.retentionNotification.header") }}</h4>
                <div class="flex row items-center">
                    <ToggleSwitch
                        :id="`project-config_input-switch_retention-notification-${currentProject?.id ?? 0}`"
                        v-model="projectParticipantDeletionNotification"
                        class="flex custom-switch ml-0 md:ml-2"
                    />
                    <TooltipButton
                        :content="t('project-configuration.retentionNotification.explained')"
                        background-color="white"
                    />
                </div>
            </div>
        </div>
    </Panel>
</template>

<style scope>
.input-switch-container {
    display: flex !important;
    flex-direction: column !important;
    align-items: left;
}

.switch-label {
    font-size: 12px;
    margin-bottom: 5px;
    color: #7e868d;
}

.custom-switch {
    margin-top: 0%;
}

.p-fieldset legend {
    font-weight: normal;
}

.p-panel-header {
    cursor: pointer;
}

.textarea-length {
    position: absolute;
    bottom: 5px;
    right: 10px;
    font-size: 12px;
    color: #888;
}

.invalid-textarea {
    border: 2px solid red !important;
}

.flex-start-top {
    align-self: flex-start;
    margin: 0 auto auto auto;
}

.input-box .p-inputnumber-input {
    max-width: 140px;
    min-width: 120px;
}

.input-box .p-datepicker-input {
    max-width: 140px;
    min-width: 100px;
}

.input-box {
    min-width: 140px;
}

.retention-period-box {
    margin-top: 6px;
}

.text-red {
    color: red !important;
}

@media (max-width: 768px) {
    .retention-period-box {
        margin-top: 0.5rem;
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>
